<template>
<div class="qr-code-container">
  <div class="code-header">
    <div class="code-title">{{ 성명 }}</div>
    <div class="code-sub-title-container">
      <div v-for="subTitle in subTitles" :key="subTitle.title" class="code-sub-title">
        <div>{{ subTitle.title}} : </div>
        <div>{{ subTitle.value }}</div>
      </div>
    </div>
  </div>
  <div ref="qrIdentityContainer" >
    <!-- QR 코드 만료 시 -->
    <div v-if="!remainSeconds" class="qr-expired">
      <!-- QR 다시 만들기 버튼 -->
      <div class="expired-text">{{ $t('QR 다시 만들기') }}</div>
      <vs-button class="retry-button" color="primary" type="border" icon-pack="feather" icon="icon-refresh-cw" size="large" @click="createQrCode"/>
    </div>

    <!-- QR 코드 유효 시 -->
    <div v-else class="qr-code-wrapper">
      <div>
        <!-- 디버깅용 - QR 코드 텍스트 확인 -->
        <div v-if="!code" style="color: red; text-align: center; padding: 20px;">{{ $t('QR 코드 데이터가 없습니다') }}</div>
        <VueQRCodeComponent v-else :text="code" :size="qrCodeSize" class="qr-code" />
      </div>
    </div>
  </div>
  <div>
    <div>
      <p v-if="remainSeconds" class="flex items-center justify-center" style="margin-bottom: 14px;">
        <feather-icon class="mr-2" icon="AlertCircleIcon"/>
        <span class="mr-4">{{ $t('남은시간') }}</span>
        <span style="color:#E42728; font-weight: bold">{{remainSeconds}}초</span>
      </p>
      <p v-else class="flex items-center justify-center" style="margin-bottom: 14px;">
        <feather-icon style="color:#E42728;" class="mr-2" icon="AlertTriangleIcon"/>
        <span style="color:#E42728; font-weight: bold">{{ $t('QR 만료됨') }}</span>
      </p>
    </div>
    <div style="margin-bottom: 16px;">
      <vs-button class="w-full" color="primary" @click="onClick">{{ $t('닫기') }}</vs-button>
    </div>
  </div>
</div>
</template>

<script>
import VueQRCodeComponent from "vue-qrcode-component";
import HttpRequest from "@/util/HttpRequest";

export default {
  name: 'QrIdentityCode',
  components: {
    VueQRCodeComponent
  },
  emits: ['close'],
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // QR 코드를 전달받은 시각
      qrCreatedAt: 0,
      // QR 코드
      code: '',
      qrCodeSize: 256,

      remainSeconds: 0,
      // 타이머 인터벌
      timerInterval: null,

      // QR 코드 갱신 주기 (초)
      qrRefreshSec: 30,
    }
  },
  computed: {
    projectId() {
      return this.$route.params.proj_id;
    },
    성명() {
      return this.$store.state.member.memberInfo.name;
    },
    title() {
      const titleList = [];
      if (this.작업허가명) {
        titleList.push(this.작업허가명);
      }
      if (this.허가번호) {
        titleList.push(this.허가번호);
      }
      return titleList.join(', ');
    },
    subTitles() {
      const subTitleList = [];

      const memberInfo = this.$store.state.member.memberInfo;
      if (!memberInfo) return [];

      const 업체명 = (memberInfo.company ? (memberInfo.company.com_name || '-') : '-');
      subTitleList.push({ title: '업체명', value: 업체명 });

      const 직책 = memberInfo.rank || '-';
      subTitleList.push({ title: '직책', value: 직책 });

      const 직종 = memberInfo.section || '-';
      subTitleList.push({ title: '직종', value: 직종 });

      // TODO 테스트용 데이터 (실제 데이터로 변경해야 함)
      subTitleList.push({title: '건설기계 조종사 면허', value: '지역00-1234-5678-90'})
      subTitleList.push({title: '장비명', value: '테스트 장비'})
      subTitleList.push({title: '규격', value: '테스트 규격'})
      subTitleList.push({title: '장비번호', value: '테스트 장비번호'})

      return subTitleList;
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.updateQrIdentityCodeSize();

        // 화면이 표시될 때마다 QR 코드 생성 (만료되었거나 없을 때)
        if (!this.remainSeconds || !this.code) {
          this.createQrCode();
        }
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.updateQrIdentityCodeSize();
    })
    window.addEventListener('resize', this.updateQrIdentityCodeSize);
    this.timerInterval = setInterval(() => {
      this.updateRemainSeconds();
    }, 100);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateQrIdentityCodeSize);
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  },
  methods: {
    onClick() {
      this.$emit('close');
    },

    createQrCode() {
      const params = {
        type: 'identity',
      };

      if (this.projectId) {
        const api = `/api/v1/project/qr/${this.projectId}/code`;

        HttpRequest.get(api, { params }).then(response => {
          const {data} = response;
          console.log('QR 코드 데이터:', data); // 디버깅용
          this.qrCreatedAt = new Date().getTime();
          this.remainSeconds = this.qrRefreshSec;
          this.code = data;
        }).catch(err => {
          console.error(err);
          const customAlert = new CustomEvent("customAlert", {
            detail: {
              title: "QR 코드 생성 실패",
              message: "QR 코드 생성에 실패했습니다. 잠시 후 다시 시도해주세요.",
            },
          });
          window.dispatchEvent(customAlert);
          this.onClick();
        })
      } else {
        const customAlert = new CustomEvent("customAlert", {
          detail: {
            title: "QR 코드 생성 실패",
            message: "프로젝트 정보가 없습니다.",
          },
        });
        window.dispatchEvent(customAlert);

        this.onClick();
      }
    },

    updateQrIdentityCodeSize() {
      const container = this.$refs.qrIdentityContainer;

      if (container) {
        const width = container.clientWidth; // 컨테이너 폭
        const height = container.clientHeight; // 컨테이너 높이

        // 폭과 높이의 90% 계산
        const maxWidth = Math.floor(width * 0.9);
        const maxHeight = Math.floor(height * 0.9);

        // 더 작은 값 선택
        const qrSize = Math.min(maxWidth, maxHeight);

        // 크기 설정
        this.qrCodeSize = qrSize;

        // CSS 변수에 동적 크기 반영
        container.style.setProperty('--qr-size', `${qrSize}px`);
      }
    },


    updateRemainSeconds() {
      const now = new Date().getTime();
      let diff = now - this.qrCreatedAt;
      if (diff > this.qrRefreshSec * 1000) {
        diff = this.qrRefreshSec * 1000;
      }
      this.remainSeconds = this.qrRefreshSec - Math.floor(diff / 1000);
    }
  }
}
</script>

<style scoped lang="scss">
.qr-code-container {
  display: grid;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr auto;

  .code-header {
    display: flex;
    flex-direction: column;

    .code-title {
      text-align: center;
      font-size: 20px;
      font-weight: 700;
      margin-top: 16px;
      margin-bottom: 12px;
    }
    .code-sub-title-container {
      padding: 0 18px;
      font-size: 15px;
      color: #3c3c3c;

      .code-sub-title {
        display: flex;
        flex-direction: row;
        margin-bottom: 8px;
        div {
          &:first-child {
            font-weight: 700;
            margin-right: 4px;
          }
        }
      }
    }
  }

  .qr-code-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;

    & > div {
      background-color: #fff !important;
      -webkit-forced-color-adjust: none !important;
      forced-color-adjust: none !important;
      color-scheme: light !important;
      -webkit-filter: none !important;
      filter: none !important;

      padding: 10px;
    }
  }

  .qr-code {
    width: var(--qr-size, 256px); // 58%; /* 컨테이너 높이의 60% */
    height: var(--qr-size, 256px); /* 컨테이너 높이의 60% */
    max-width: 100%; /* 최대 크기 제한 */
    max-height: 100%; /* 최대 크기 제한 */
    background-color: white;


    &::v-deep img {
      width: 100%;
      height: var(--qr-size, 256px);
      padding: 10px;
      border: 1px solid #ccc;
    }
  }

  .qr-expired {
    display: flex;
    height: 100%;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
  }

  .expired-icon {
    color: #ff4d4f;
  }

  .expired-text {
    font-size: 14px;
    color: #3c3c3c;
  }

  .retry-button {
    margin-top: 10px;
    padding: 60px 64px;
    font-size: 14px;
    font-weight: 500;
    border-radius: 6px;
    transition: all 0.2s;
  }
}
</style>
